/**
 * BCA §5.1.3 washroom clear-size measurement orchestrator.
 */

import { measureClearAABB } from "./measureClearAABB";
import {
  classifyByNodeName,
  elementWorldBox,
  makeRayCaster,
  sleep,
} from "./rayCast";
import type { AccessibleWcCandidate } from "./types";
import { FT_TO_MM, RAY_COUNT, type WashroomMeasureResult } from "./types";

interface MeasureOptions {
  /** Skip setView jumps — caller already staged the model. */
  skipCameraStaging?: boolean;
}

async function measureOne(
  viewer: Autodesk.Viewing.GuiViewer3D,
  candidate: AccessibleWcCandidate,
  options: MeasureOptions = {}
): Promise<WashroomMeasureResult> {
  const model = viewer.model;
  const tree = model.getInstanceTree();
  if (!tree) {
    return emptyResult(candidate);
  }

  const nm = (id: number) => tree.getNodeName(id) ?? "";
  const castRay = makeRayCaster(viewer, classifyByNodeName(nm));
  const unitScale = model.getUnitScale?.() ?? 1;
  const bbox = elementWorldBox(viewer, candidate.dbId);
  if (!bbox) {
    return emptyResult(candidate);
  }

  viewer.isolate([], model);
  viewer.showAll();

  if (!options.skipCameraStaging) {
    viewer.fitToView([candidate.dbId], undefined, true);
    await sleep(400);
  }

  let geometry = null;
  let rayOrigin: THREE.Vector3 | undefined;
  for (const waitMs of [0, 300, 600]) {
    if (waitMs > 0) await sleep(waitMs);
    const origin = bbox.getCenter(new THREE.Vector3());
    origin.z += 0.75 / unitScale;
    rayOrigin = origin.clone();
    geometry = measureClearAABB(origin, castRay, {
      rays: RAY_COUNT,
      maxDist: 6 / unitScale,
      marchStep: 0.05 / unitScale,
    });
    if (geometry) break;
  }

  if (!geometry) {
    return emptyResult(candidate);
  }

  const wMm = Math.round(geometry.width * FT_TO_MM);
  const dMm = Math.round(geometry.depth * FT_TO_MM);
  return {
    candidate,
    widthMm: wMm,
    depthMm: dMm,
    shortMm: Math.min(wMm, dMm),
    longMm: Math.max(wMm, dMm),
    geometry,
    rayOrigin,
  };
}

function emptyResult(candidate: AccessibleWcCandidate): WashroomMeasureResult {
  return {
    candidate,
    widthMm: null,
    depthMm: null,
    shortMm: null,
    longMm: null,
    geometry: null,
  };
}

export async function measureClearAtDbId(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number,
  displayName: string,
  options?: MeasureOptions
): Promise<WashroomMeasureResult> {
  return measureOne(viewer, { dbId, displayName }, options);
}

export async function measureAccessibleWashrooms(
  viewer: Autodesk.Viewing.GuiViewer3D,
  candidates: AccessibleWcCandidate[],
  options?: MeasureOptions
): Promise<WashroomMeasureResult[]> {
  const results: WashroomMeasureResult[] = [];
  for (const c of candidates) {
    results.push(await measureOne(viewer, c, options));
  }
  return results;
}
